<script setup lang="ts">
import { type RangeSelection } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted } from "vue";

import { getPropertyWithConfigList } from "~/common/properties";
import {
  EVENT_CREATE_DOC_DOC_RELATIONSHIP,
  EVENT_CREATE_TASK_DOC_RELATIONSHIP,
  EVENT_CREATE_TASK_TASK_RELATES_TO_RELATIONSHIP,
  EVENT_SUBSCRIBE_USER,
} from "~/components/text/const";
import { $createDartLinkNode } from "~/components/text/nodes/DartLinkNode";
import { $createEntityNode, EntityNode } from "~/components/text/nodes/EntityNode";
import { insertNodeAtSelection } from "~/components/text/utils";
import { UNASSIGNED_PSEUDO_ASSIGNEE_KEY } from "~/components/visualization/constants";
import { getDocUrl, getTaskUrl } from "~/router/common";
import { EntityName, PropertyKind, TypeaheadSpecialEntity } from "~/shared/enums";
import type { Property, PropertyConfig, TypeaheadGetOptionsFn, TypeaheadOption } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { makeStringComparator, propertyObjectComparator } from "~/utils/comparator";

import { removeConflictingEntitiesAndSmartMatches } from "./common";
import { createTypeaheadSections } from "./links";
import TypeaheadPlugin from "./TypeaheadPlugin.vue";

const props = defineProps<{
  hasFocus: boolean;
  mentionsOnly?: boolean;
  includeUnassigned?: boolean;
  excludeDartLinks?: boolean;
  subscribeMentionsSeparately?: boolean;
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const editor = useLexicalComposer();

const isDoc = editor._config.namespace.startsWith("doc");

const getTypeaheads = (properties: [Property, PropertyConfig][]) =>
  properties
    .filter(([, config]) => config.typeahead !== null)
    .flatMap(
      ([property, config]) =>
        config
          .typeahead?.(property)
          ?.filter((e) => e.trigger.trim() !== "")
          ?.map((e) => ({
            ...e,
            property,
            getOptions: e.getOptions.map((original) => async (query: string) => {
              const result = await original(query);
              return { ...result, title: result.title };
            }),
          })) ?? []
    );

const typeaheads = computed(() => {
  const properties = getPropertyWithConfigList();
  const assigneeProperty = properties.filter(([property]) => property.duid === dataStore.defaultAssigneesProperty.duid);
  const assigneeTypeahead = getTypeaheads(assigneeProperty)[0];
  const atTypeahead = {
    ...assigneeTypeahead,
    trigger: "@",
    getOptions: assigneeTypeahead.getOptions.map((original) => async (query) => {
      const { options, key, title } = await original(query);
      const filteredOptions = options.filter(
        (e) => e.value !== UNASSIGNED_PSEUDO_ASSIGNEE_KEY || props.includeUnassigned
      );
      const sectionTitle = props.excludeDartLinks ? "Users" : title;
      return { key, title: sectionTitle, options: filteredOptions, total: filteredOptions.length };
    }) satisfies TypeaheadGetOptionsFn[],
  };

  if (!props.excludeDartLinks) {
    const { currentPage } = appStore;
    createTypeaheadSections(currentPage).forEach(({ getOptions, title }) => {
      if (title) {
        atTypeahead.getOptions.push(async (query) => {
          const typeaheadResult = await getOptions(query);
          return { ...typeaheadResult, title };
        });
      }
    });
  }

  if (props.mentionsOnly) {
    return [atTypeahead];
  }

  // Only include one typeahead per trigger
  const otherProperties = properties
    .filter(([property]) => property.duid !== dataStore.defaultAssigneesProperty.duid)
    .sort(propertyObjectComparator);
  const otherTypeaheads = [
    ...getTypeaheads(otherProperties)
      .reduce((acc, e) => {
        if (acc.has(e.trigger)) {
          return acc;
        }
        acc.set(e.trigger, e);
        return acc;
      }, new Map<string, typeof atTypeahead>())
      .values(),
  ];

  const allTypeaheads = [atTypeahead].concat(otherTypeaheads);
  // Can't directly add to res because it infinite loops; this way the original array is separate and this returns a clone
  return allTypeaheads.concat([
    {
      trigger: "/",
      isMultiple: true,
      property: null as unknown as Property,
      getOptions: allTypeaheads
        .filter((e) => e.property.kind !== PropertyKind.USER)
        .flatMap((e) => ({ getOption: e.getOptions, property: e.property }))
        .flatMap(({ getOption, property }) =>
          getOption.map((original) => async (query) => {
            const { options, key, title } = await original(query);
            return {
              key,
              title: title || property?.title || key,
              options: [...options.slice(0, 1), ...options.slice(1).sort(makeStringComparator((e) => e.label))],
              total: options.length,
            };
          })
        ),
    },
  ]);
});

const onSelect = async (selection: RangeSelection, option: TypeaheadOption) => {
  // Handle links
  if (props.mentionsOnly && !props.excludeDartLinks && typeof option.value === "string") {
    if (option.specialEntity === TypeaheadSpecialEntity.TASK) {
      const task = await dataStore.getTaskByDuid(option.value);
      if (task) {
        if (!isDoc) {
          editor.dispatchCommand(EVENT_CREATE_TASK_TASK_RELATES_TO_RELATIONSHIP, task.duid);
        } else {
          editor.dispatchCommand(EVENT_CREATE_TASK_DOC_RELATIONSHIP, {
            entityDuid: task.duid,
            specialEntity: TypeaheadSpecialEntity.TASK,
          });
        }

        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getTaskUrl(task), EntityName.TASK, task.duid), selection);
        });
        return;
      }
    }

    if (option.specialEntity === TypeaheadSpecialEntity.DOC) {
      const doc = await dataStore.getDocByDuid(option.value);
      if (doc) {
        if (!isDoc) {
          editor.dispatchCommand(EVENT_CREATE_TASK_DOC_RELATIONSHIP, {
            entityDuid: doc.duid,
            specialEntity: TypeaheadSpecialEntity.DOC,
          });
        } else {
          editor.dispatchCommand(EVENT_CREATE_DOC_DOC_RELATIONSHIP, doc.duid);
        }

        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getDocUrl(doc), EntityName.DOC, doc.duid), selection);
        });
        return;
      }
    }
  }

  if (
    props.mentionsOnly &&
    typeof option.value === "string" &&
    option.value !== UNASSIGNED_PSEUDO_ASSIGNEE_KEY &&
    !props.subscribeMentionsSeparately
  ) {
    const user = dataStore.getUserByDuid(option.value);
    if (user) {
      editor.dispatchCommand(EVENT_SUBSCRIBE_USER, user);
    }
  }

  if (!option.property) {
    return;
  }

  const entityNode = $createEntityNode(option.property.duid, option.value);
  insertNodeAtSelection(entityNode, selection);

  if (!props.mentionsOnly || props.includeUnassigned) {
    removeConflictingEntitiesAndSmartMatches(editor, entityNode);
  }
};

onMounted(() => {
  if (!editor.hasNodes([EntityNode])) {
    throw new Error("EntityTypeaheadPlugin: EntityNode not registered on editor");
  }
});
</script>

<template>
  <TypeaheadPlugin
    v-for="typeahead in typeaheads"
    :key="typeahead.trigger"
    :trigger="typeahead.trigger"
    :get-options-fns="typeahead.getOptions"
    :wide="!excludeDartLinks || pageStore.isMobile"
    @select="onSelect" />
</template>
